/**
 * External Imports
 */
import { FC, useCallback, useMemo, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

/**
 * Internal Imports
 */
import { MarkCompleteBtn } from '../../../shared/buttons';
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import {
  openModal,
  setModalType,
  setModalAction,
  selectDataForEdit,
  clearFeedbackFilter,
  setTaskSort
} from '../../../../redux/actions';

import { useUserRolesRef } from '../../../../services/UserRoles';

import { useFeedbackApi } from '../../../../utils/api';
import { useDebounceValue } from '../../../../utils/debouncers';
import { date, text, abbreviatedName } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Filter,
  Feedback,
  EmpDepartment
} from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  StyledBoldMoreLabel,
  StyledCategoryContainer,
  StyledLocationContainer,
  StyledPlusIcon,
  StyledSpan
} from './FeedbackTable.styles';
import {
  CellContainer,
  StyledBoldLabel,
  StyledLabel,
  StyledNoWrapLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';

interface IFeedbackTableProps {
  authorizedUser: AuthUserProps;
  currentFeedbackPage: number;
  feedbackFilter: Filter;
  loadingFeedbackByUnitIds: boolean;
  loadingFeedbackByEmployeeList: boolean;
  loadingFeedbackListByFacility: boolean;
  location: string;
  searchTerm: string;
  selectedDelegate: string;
  selectedUnits: { id: string; unit: string }[];
  selectedEmpDepartments: EmpDepartment[];
  storeFeedback: Feedback[];
  totalCount: number;
  units: { unitId: string; unit: string }[];
}

const FeedbackTable: FC<IFeedbackTableProps> = ({
  authorizedUser,
  currentFeedbackPage,
  feedbackFilter,
  loadingFeedbackByUnitIds,
  loadingFeedbackByEmployeeList,
  loadingFeedbackListByFacility,
  location,
  searchTerm,
  selectedDelegate,
  selectedUnits,
  selectedEmpDepartments,
  storeFeedback,
  totalCount,
  units
}) => {
  const dispatch = useDispatch();
  const [sortKey, setSortKey] = useState<string>('created');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [active, setActive] = useState<string>('Created');
  const [loading, setLoading] = useState<boolean>(true);

  const { isEnterpriseAdmin } = useUserRolesRef();

  const dbSearchTerm = useDebounceValue(searchTerm.trim(), 350);

  const { from = '' } = useLocation().state || {};

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    const feedbackAction = await useFeedbackApi({
      authorizedUser,
      currentFeedbackPage,
      feedbackFilter,
      searchTerm: dbSearchTerm && dbSearchTerm.length > 2 ? dbSearchTerm : '',
      selectedDelegate,
      selectedUnits,
      selectedEmpDepartments,
      sortDir,
      sortKey,
      summaryView: from ? from.split('/')[0] : undefined
    });
    dispatch(feedbackAction);
    setLoading(false);
  }, [
    authorizedUser,
    currentFeedbackPage,
    dbSearchTerm,
    feedbackFilter,
    from,
    selectedDelegate,
    selectedUnits,
    selectedEmpDepartments,
    sortDir,
    sortKey
  ]);

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Created':
        key = 'created';
        break;
      case 'Department':
      case 'Location':
        key = 'location';
        break;
      case 'Category/Subcategory':
        key = 'category';
        break;
      case 'Patient':
        key = 'patientName';
        break;
      case 'Employee':
        key = 'employeeName';
        break;
      case 'Last Comment':
        key = 'lastComment';
        break;
      case 'Attached':
        key = 'attachedTo';
        break;
      case 'Status':
        key = 'status';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openFeedback = useCallback(
    (e: React.BaseSyntheticEvent<MouseEvent>, fbRow: Feedback) => {
      if (e.target.id !== 'btn-complete') {
        dispatch(setModalType('Add Feedback'));
        dispatch(selectDataForEdit(fbRow));
        // Edit modal
        if (
          !fbRow.completed &&
          (isEnterpriseAdmin ||
            (fbRow.userId.toLowerCase() ===
              authorizedUser?.hcaid.toLowerCase() &&
              fbRow.taskDate &&
              !date.isOverOneDayOld(fbRow.taskDate)))
        ) {
          dispatch(setModalAction('EDIT'));
          dispatch(openModal());
          // Partial edit modal
        } else if (
          !fbRow.completed &&
          fbRow.userId.toLowerCase() === authorizedUser?.hcaid.toLowerCase() &&
          fbRow.taskDate &&
          date.isOverOneDayOld(fbRow.taskDate)
        ) {
          dispatch(setModalAction('PARTIAL-EDIT'));
          dispatch(openModal());
        } else {
          // View modal
          dispatch(setModalAction('VIEW'));
          dispatch(openModal());
        }
      }
    },
    [authorizedUser]
  );

  const isTableEmpty = useMemo(
    () => !dbSearchTerm && storeFeedback.length === 0,
    [dbSearchTerm, storeFeedback]
  );

  const isTableEmptySearch = useMemo(
    () => dbSearchTerm && storeFeedback.length === 0,
    [dbSearchTerm, storeFeedback]
  );

  // Reset page when navigate away
  useEffect(() => {
    return () => {
      dispatch(clearFeedbackFilter());
      dispatch(setTaskSort({ sortKey: 'created', sortDir: 'desc' }));
    };
  }, []);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  useEffect(() => {
    dispatch(setTaskSort({ sortKey, sortDir }));
  }, [sortDir, sortKey]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Created"
      id="Feedback-Table-Created-Column"
      unit="column"
      colWidth={11}
      direction={!(active === 'Created') ? 'none' : sortDir}
      sort
    >
      Created
    </CellContainer>,
    <CellContainer
      key="Location"
      id="Feedback-Table-Location-Column"
      unit="column"
      colWidth={location === 'patient' ? 9 : 13}
      direction={
        !(active === 'Location' || active === 'Department') ? 'none' : sortDir
      }
      sort
    >
      {location === 'patient' ? 'Location' : 'Department'}
    </CellContainer>,
    <CellContainer
      key="Type"
      id="Feedback-Table-Type-Column"
      direction="none"
      unit="column"
      colWidth={8}
    >
      Type
    </CellContainer>,
    <CellContainer
      key="Category"
      id="Feedback-Table-Category-Column"
      unit="column"
      colWidth={location === 'patient' ? 18 : 19}
      direction={!(active === 'Category/Subcategory') ? 'none' : sortDir}
      sort
    >
      Category/Subcategory
    </CellContainer>,
    <CellContainer
      key="Description"
      id="Feedback-Table-Description-Column"
      unit="column"
      colWidth={location === 'patient' ? 17 : 23}
      direction="none"
    >
      Description
    </CellContainer>,
    <CellContainer
      key="Employee"
      id="Feedback-Table-Employee-Column"
      unit="column"
      colWidth={location === 'patient' ? 12 : 13}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      Employee
    </CellContainer>
  ];

  if (location === 'patient') {
    headers.push(
      <CellContainer
        key="Patient"
        id="Feedback-Table-Patient-Column"
        unit="column"
        colWidth={10}
        direction={!(active === 'Patient') ? 'none' : sortDir}
        sort
      >
        Patient
      </CellContainer>
    );
  }
  headers.push(
    <CellContainer
      key="Status"
      id="Feedback-Table-Status-Column"
      unit="column"
      colWidth={location === 'patient' ? 15 : 13}
      direction={!(active === 'Status') ? 'none' : sortDir}
      sort
    >
      Status
    </CellContainer>
  );

  const rows = storeFeedback?.map((fb: Feedback, i: number) => (
    <StyledNeuTableRow
      // eslint-disable-next-line react/no-array-index-key
      key={`feedback-row-${i}`}
      data-testid={`feedback-row-${fb.taskId}`}
      unit="row"
      size="default"
      height="7em"
      role="button"
      styles={fb.completed ? 'background-color: #f6f6f6 !important' : ''}
      onClick={(e: React.BaseSyntheticEvent<MouseEvent>) => openFeedback(e, fb)}
    >
      <CellContainer unit="column" colWidth={11} alignment="start">
        <StyledBoldLabel>{fb.userFullName}</StyledBoldLabel>
        <StyledLabel>
          {fb.taskDate ? date.dayOfYrFull(fb.taskDate) : ''}
        </StyledLabel>
      </CellContainer>
      <CellContainer
        unit="column"
        colWidth={location === 'patient' ? 9 : 13}
        alignment="start"
      >
        <StyledLocationContainer>
          {location === 'patient' ? (
            <>
              <StyledBoldLabel className="neu-text-gray-90">
                {units.find(unit => unit.unitId === fb.unitId)?.unit ?? ''}
              </StyledBoldLabel>
              <StyledLabel>
                {fb.room ? `${fb.room}${fb.bed || ''}` : ''}
              </StyledLabel>
            </>
          ) : (
            <StyledBoldLabel className="neu-text-gray-90">
              {fb?.department ? fb.department : 'Unknown'}
            </StyledBoldLabel>
          )}
        </StyledLocationContainer>
      </CellContainer>
      <CellContainer unit="column" colWidth={8} alignment="start">
        <StyledLabel className="neu-text-gray-90">
          {text.capFirst(fb.taskSubtype)}
        </StyledLabel>
      </CellContainer>
      <CellContainer
        unit="column"
        colWidth={location === 'patient' ? 18 : 19}
        alignment="start"
      >
        <StyledCategoryContainer>
          {fb.taskCategory.length > 0 ? (
            <>
              <StyledNoWrapLabel>
                <StyledBoldLabel>
                  {fb.taskCategory[0] && fb.taskCategory[0]}
                </StyledBoldLabel>
              </StyledNoWrapLabel>
              {fb.taskCategory[1] && (
                <div className="px-0">
                  <StyledNoWrapLabel>
                    <StyledBoldLabel>
                      {fb.taskCategory[1] && fb.taskCategory[1]}
                    </StyledBoldLabel>
                  </StyledNoWrapLabel>
                  {fb.taskCategory.length > 2 && (
                    <StyledBoldMoreLabel className="neu-text-blue-60">
                      <StyledSpan> (</StyledSpan>
                      <StyledPlusIcon color="plain-0" size="1x">
                        add
                      </StyledPlusIcon>
                      <StyledSpan>
                        {fb.taskCategory.slice(2).length})
                      </StyledSpan>
                    </StyledBoldMoreLabel>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <StyledBoldLabel className="neu-text-gray-90">
                {fb.category}
              </StyledBoldLabel>
              <StyledLabel className="neu-text-gray-90">
                {fb.subCategory ? fb.subCategory : ''}
              </StyledLabel>
            </>
          )}
        </StyledCategoryContainer>
      </CellContainer>
      <CellContainer
        unit="column"
        colWidth={location === 'patient' ? 17 : 23}
        alignment="start"
      >
        <StyledLabel className="neu-text-gray-90 text-clamp text-clamp__3">
          {fb.description}
        </StyledLabel>
      </CellContainer>
      <CellContainer
        unit="column"
        colWidth={location === 'patient' ? 12 : 13}
        alignment="start"
      >
        <StyledBoldLabel className="neu-text-gray-90">
          {fb.employeeFullName ? fb.employeeFullName : ''}
        </StyledBoldLabel>
        <StyledLabel>{fb.employeeId ? fb.employeeId : ''}</StyledLabel>
      </CellContainer>
      {location === 'patient' && (
        <CellContainer unit="column" colWidth={10} alignment="start">
          <StyledBoldLabel className="neu-text-gray-90">
            {fb.roundingType === 'patient' &&
            fb.roundId &&
            (fb.patientFirstName === '' || fb.patientLastName === '')
              ? 'Unknown'
              : abbreviatedName(fb.patientFirstName, fb.patientLastName)}
          </StyledBoldLabel>
        </CellContainer>
      )}
      <CellContainer
        unit="column"
        colWidth={location === 'patient' ? 15 : 13}
        alignment="start"
      >
        <StyledBoldLabel className="neu-text-gray-90 mb-2 bold">
          {fb.completed ? 'Completed' : 'Opened'}
        </StyledBoldLabel>
        <StyledLabel>
          {fb.completed && fb.completedDate ? ( // Add check for completedDate some data coming back with null
            date.dayOfYrFull(fb.taskDate)
          ) : !fb.completed &&
            (isEnterpriseAdmin ||
              fb.userId.toLowerCase() ===
                authorizedUser?.hcaid.toLowerCase()) ? (
            <MarkCompleteBtn taskId={fb.taskId} taskType="feedback" />
          ) : null}
        </StyledLabel>
      </CellContainer>
    </StyledNeuTableRow>
  ));

  return (
    <Table
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableEmptySearch}
      loading={
        loading ||
        loadingFeedbackByUnitIds ||
        loadingFeedbackByEmployeeList ||
        loadingFeedbackListByFacility
      }
      totalResults={totalCount}
      onSort={(e: any) => handleSort(e.detail.label)}
      type="feedback"
    />
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser?.authorizedUser,
  currentFeedbackPage: state.TaskReducer.currentFeedbackPage,
  feedbackFilter: state.TaskReducer.feedbackFilter,
  loadingFeedbackByUnitIds: state.TaskReducer.loadingFeedbackByUnitIds,
  loadingFeedbackByEmployeeList:
    state.TaskReducer.loadingFeedbackByEmployeeList,
  loadingFeedbackListByFacility: state.TaskReducer.loadingFeedbackByFacility,
  location: state.UserReducer.userSection,
  selectedDelegate: state.EmployeeReducer.selectedDelegatedEmployee.hcaid,
  selectedUnits: state.ConfigReducer.selectedUnits,
  selectedEmpDepartments: state.ConfigReducer.selectedEmpDepartments,
  storeFeedback: state.TaskReducer.feedback,
  totalCount: state.TaskReducer.totalCount,
  units: state.ConfigReducer.units
});

export default connect(mapReduxStateToProps)(FeedbackTable);
